import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpenCheck,
  ChevronDown,
  Loader2,
  Mail,
  MessageCircleQuestion,
  Mic,
  Phone,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { API } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagContext';

/**
 * The floating ERP assistant.
 *
 * It lives on every screen rather than on the dashboard, because the moment
 * someone has a question is the moment they are already standing in front of
 * some other page — walking back to the dashboard to ask is exactly the friction
 * this is meant to remove.
 *
 * It is built as a *conversation*, not a report viewer. Two things follow from
 * that and both are load-bearing for someone who is not comfortable with
 * software:
 *
 *  1. There is never a blank input staring back. A strip of tappable questions
 *     sits above the box at all times — the follow-ups from the last answer, or
 *     the starter set. The whole tool is usable without typing a character.
 *  2. Answers arrive as chat bubbles from a named assistant, with a typing
 *     pause before them. A card that appears instantly reads as a form result;
 *     a bubble that is being written reads as someone answering you.
 */

const STORAGE_KEY = 'erp-assistant-thread-v1';
const MAX_STORED = 12;

// The pause before an answer appears. Long enough to read as "thinking",
// short enough that nobody waits on it: most answers take longer than this to
// come back from the server anyway, in which case it costs nothing at all.
const TYPING_MS = 420;

// Fact tiles and status pills share one palette so a red number means the same
// thing wherever it appears.
const TONES = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warn: 'border-amber-200 bg-amber-50 text-amber-700',
  bad: 'border-rose-200 bg-rose-50 text-rose-700',
  neutral: 'border-slate-200 bg-white text-slate-800',
};

const PILL_TONES = {
  good: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  bad: 'bg-rose-100 text-rose-700',
  neutral: 'bg-slate-100 text-slate-600',
};

const readStoredThread = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.slice(-MAX_STORED) : [];
  } catch {
    return [];
  }
};

/** Browser dictation, when the browser has it. Absent on Firefox — hence the
 *  capability check rather than a button that silently does nothing. */
const getSpeechRecognition = () =>
  (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
  null;

function Chip({ label, onClick, tone = 'plain' }) {
  const styles =
    tone === 'accent'
      ? 'border-cyan-200 bg-cyan-50 text-cyan-800 hover:border-cyan-400 hover:bg-cyan-100'
      : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-medium shadow-sm transition ${styles}`}
    >
      {label}
    </button>
  );
}

/** `**bold**` from the server, and nothing else.
 *
 *  The one piece of formatting worth carrying: a sidebar trail
 *  ("Masters → Production Masters") has to stand out from the sentence around
 *  it, or the answer reads as a paragraph and the operator has to hunt for the
 *  path inside it. Deliberately not a markdown renderer — this text is written
 *  by our own handlers, and a parser here would be a way to render whatever a
 *  record happens to contain. */
function Emphasis({ text }) {
  const parts = String(text ?? '').split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 ? (
          <strong key={index} className="font-semibold text-slate-900">
            {part}
          </strong>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

function FactTile({ fact }) {
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 ${TONES[fact.tone] || TONES.neutral}`}>
      <div className="truncate text-[10px] font-semibold uppercase tracking-wide opacity-70">
        {fact.label}
      </div>
      <div className="mt-0.5 truncate text-sm font-bold leading-tight">{fact.value}</div>
      {fact.hint ? <div className="truncate text-[10px] opacity-70">{fact.hint}</div> : null}
    </div>
  );
}

function Cell({ value }) {
  // A cell is either plain text or `{text, tone}` for anything that carries a
  // status — the tone is decided server-side, where the domain rules live.
  if (value && typeof value === 'object') {
    return (
      <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${PILL_TONES[value.tone] || PILL_TONES.neutral}`}>
        {value.text}
      </span>
    );
  }
  return <span>{value ?? '—'}</span>;
}

function AnswerTable({ table }) {
  if (!table?.columns?.length || !table?.rows?.length) return null;
  const hidden = Math.max(0, (table.total || 0) - (table.shown || 0));
  return (
    <div className="mt-2.5">
      {/* The table is the one thing here that can be wider than the panel, so it
          scrolls inside its own box instead of stretching the chat column. */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[420px] border-collapse text-[11px]">
          <thead>
            <tr className="bg-slate-50">
              {table.columns.map((col) => (
                <th
                  key={col.key}
                  className={`whitespace-nowrap px-2 py-1.5 font-semibold text-slate-500 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, index) => (
              <tr key={index} className="border-t border-slate-100">
                {table.columns.map((col) => (
                  <td
                    key={col.key}
                    className={`whitespace-nowrap px-2 py-1.5 text-slate-700 ${col.align === 'right' ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    <Cell value={row[col.key]} />
                  </td>
                ))}
              </tr>
            ))}
            {table.total_row ? (
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                {table.columns.map((col) => (
                  <td
                    key={col.key}
                    className={`whitespace-nowrap px-2 py-1.5 text-slate-800 ${col.align === 'right' ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    <Cell value={table.total_row[col.key]} />
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {hidden > 0 ? (
        <p className="mt-1 text-[10px] text-slate-400">
          Showing the top {table.shown} of {table.total}. Open the screen to see the rest.
        </p>
      ) : null}
    </div>
  );
}

/** One message from the assistant, drawn as a bubble beside its avatar. */
function BotBubble({ children, tone = 'plain' }) {
  const skin =
    tone === 'problem'
      ? 'border-amber-200 bg-amber-50/70'
      : 'border-slate-200 bg-white';
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-sm">
        <Sparkles className="h-3.5 w-3.5" />
      </span>
      <div
        className={`min-w-0 max-w-[calc(100%-2.25rem)] flex-1 rounded-2xl rounded-tl-md border px-3 py-2.5 shadow-sm ${skin}`}
      >
        {children}
      </div>
    </div>
  );
}

function AnswerBubble({ answer, onOpen }) {
  const isProblem = answer.kind === 'denied' || answer.kind === 'error';
  return (
    <BotBubble tone={isProblem ? 'problem' : 'plain'}>
      <h4 className="text-[13px] font-bold leading-snug text-slate-900">{answer.headline}</h4>

      {answer.lines?.filter(Boolean).length ? (
        <div className="mt-1 space-y-1">
          {answer.lines.filter(Boolean).map((line, index) => (
            <p key={index} className="text-[12px] leading-relaxed text-slate-600">
              <Emphasis text={line} />
            </p>
          ))}
        </div>
      ) : null}

      {/* Steps are numbered rather than bulleted because the order *is* the
          instruction — "open Masters, then the tab" is not the same advice
          read the other way round. */}
      {answer.steps?.length ? (
        <ol className="mt-2 space-y-1.5">
          {answer.steps.map((step, index) => (
            <li key={index} className="flex gap-2 text-[12px] leading-relaxed text-slate-700">
              <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-[9px] font-bold text-cyan-700">
                {index + 1}
              </span>
              <span className="min-w-0"><Emphasis text={step} /></span>
            </li>
          ))}
        </ol>
      ) : null}

      {answer.facts?.length ? (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {answer.facts.map((fact) => (
            <FactTile key={`${fact.label}-${fact.value}`} fact={fact} />
          ))}
        </div>
      ) : null}

      <AnswerTable table={answer.table} />

      {answer.actions?.length ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {answer.actions.map((action, index) => {
            // Contact actions are styled apart from navigation ones: reaching a
            // person is a different kind of act from opening a screen, and on a
            // phone the call button is the one that matters.
            const contact = action.kind === 'mail' || action.kind === 'tel';
            const Icon = action.kind === 'mail' ? Mail : action.kind === 'tel' ? Phone : ArrowRight;
            return (
              <button
                key={`${action.path}-${index}`}
                type="button"
                onClick={() => onOpen(action.path)}
                className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition ${
                  contact
                    ? 'border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
              >
                {action.kind === 'mail' || action.kind === 'tel' ? (
                  <Icon className="h-3 w-3" />
                ) : null}
                {action.label}
                {contact ? null : <Icon className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      ) : null}

      {answer.sources?.length ? (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
          <BookOpenCheck className="h-3 w-3" />
          Source: {answer.sources.join(' · ')}
        </div>
      ) : null}

      {answer.understood?.length ? (
        // Showing what was understood is what makes a wrong answer *correctable*:
        // the operator can see the assistant read "cold storage + today" and
        // rephrase, instead of quietly trusting a number about something else.
        <p className="mt-2 text-[10px] text-slate-400">
          Understood: {answer.understood.join(' · ')}
          {answer.confidence === 'low' ? ' — not fully sure, please confirm' : ''}
        </p>
      ) : null}
    </BotBubble>
  );
}

function TypingBubble() {
  return (
    <BotBubble>
      <span className="flex items-center gap-1 py-0.5" aria-label="Reading your records">
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
            style={{ animationDelay: `${dot * 140}ms` }}
          />
        ))}
      </span>
    </BotBubble>
  );
}

export default function AssistantWidget() {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const enabled = isEnabled('aiChatBot');

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState(readStoredThread);
  const [intro, setIntro] = useState(null);
  const [listening, setListening] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechSupported = useMemo(() => Boolean(getSpeechRecognition()), []);

  // Only the questions and which skill answered them go back to the server. The
  // answers themselves are derived from live records, so replaying them would
  // only add stale numbers — but `skill` is what lets "and then?" know which
  // task it is continuing, and it is three words long.
  const history = useMemo(
    () =>
      thread
        .slice(-6)
        .map((entry) => ({
          role: entry.role,
          content: entry.role === 'user' ? entry.text : entry.answer?.headline || '',
          skill: entry.role === 'assistant' ? entry.answer?.skill || '' : '',
        })),
    [thread],
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread.slice(-MAX_STORED)));
    } catch {
      /* a full or blocked sessionStorage must never break the chat */
    }
  }, [thread]);

  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [thread, loading, open]);

  useEffect(() => {
    if (!open || intro || !enabled) return;
    let cancelled = false;
    axios
      .get(`${API}/assistant/suggestions`)
      .then((res) => {
        if (!cancelled) setIntro(res.data);
      })
      .catch(() => {
        if (!cancelled) setIntro({ greeting: 'Hello! Ask me about your stock, lots, orders or payments.', suggestions: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [open, intro, enabled]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  // Escape closes the panel, and Ctrl/Cmd+K opens it from anywhere.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && open) setOpen(false);
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const ask = useCallback(
    async (text) => {
      const prompt = String(text ?? question).trim();
      if (!prompt || loading) return;
      setQuestion('');
      setThread((prev) => [...prev, { role: 'user', text: prompt, at: Date.now() }]);
      setLoading(true);
      const startedAt = Date.now();
      let entry;
      try {
        const res = await axios.post(`${API}/assistant/ask`, { question: prompt, history });
        entry = { role: 'assistant', answer: res.data, at: Date.now() };
      } catch (error) {
        const detail = error?.response?.data?.detail;
        entry = {
          role: 'assistant',
          at: Date.now(),
          answer: {
            kind: 'error',
            headline: 'I could not reach the records just now',
            lines: [
              typeof detail === 'string' && detail
                ? detail
                : 'Please check your connection and try again.',
            ],
            followups: [],
          },
        };
      }
      // Hold the typing bubble for a moment on a fast reply. An answer that
      // lands in 40ms reads as a lookup table; the same answer a blink later
      // reads as a reply, and nobody is kept waiting on a slow one.
      const elapsed = Date.now() - startedAt;
      if (elapsed < TYPING_MS) {
        await new Promise((resolve) => setTimeout(resolve, TYPING_MS - elapsed));
      }
      setThread((prev) => [...prev, entry]);
      setLoading(false);
    },
    [question, loading, history],
  );

  const startDictation = useCallback(() => {
    const Recognition = getSpeechRecognition();
    if (!Recognition) return;
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new Recognition();
    // en-IN handles the Hinglish and Telugu-English mixing these questions are
    // actually asked in far better than en-US does.
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const said = event.results?.[0]?.[0]?.transcript || '';
      if (said) {
        setQuestion(said);
        ask(said);
      }
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  }, [listening, ask]);

  const openPath = useCallback(
    (path) => {
      if (!path) return;
      // mailto:/tel: are handed to the OS, not to the router — pushing them
      // through navigate() lands on a blank in-app route and the mail client
      // never opens.
      if (/^(mailto:|tel:|https?:)/i.test(path)) {
        window.location.href = path;
        return;
      }
      navigate(path);
      // Closing on mobile matters: the panel is full-screen there, so leaving it
      // open would hide the very screen the operator just asked to see.
      if (window.innerWidth < 640) setOpen(false);
    },
    [navigate],
  );

  const reset = useCallback(() => {
    setThread([]);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const starters = useMemo(() => intro?.suggestions || [], [intro]);

  const grouped = useMemo(
    () =>
      starters.reduce((acc, suggestion) => {
        (acc[suggestion.group] = acc[suggestion.group] || []).push(suggestion);
        return acc;
      }, {}),
    [starters],
  );

  // The strip above the input. Follow-ups from the last answer when there are
  // any, starter questions otherwise — so there is always something to tap and
  // the operator never has to invent a sentence from nothing.
  const quickChips = useMemo(() => {
    const last = [...thread].reverse().find((entry) => entry.role === 'assistant');
    const followups = last?.answer?.followups || [];
    if (followups.length) {
      return followups.map((choice) => ({ label: choice.label, question: choice.question }));
    }
    return starters.slice(0, 8).map((s) => ({ label: s.label, question: s.question }));
  }, [thread, starters]);

  if (!enabled) return null;

  return (
    <>
      {/* Launcher. Sits above the content on every page, clear of the iOS home
          indicator on phones. */}
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Aqua ERP AI assistance"
          className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-blue-600/30 transition hover:scale-105 hover:shadow-xl active:scale-95"
          style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px) + var(--mobile-nav-offset, 0px))' }}
        >
          <MessageCircleQuestion className="h-6 w-6" />
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-300 opacity-60" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-cyan-400 ring-2 ring-white" />
          </span>
        </button>
      ) : null}

      {open ? (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-end sm:inset-auto sm:bottom-5 sm:right-5"
          role="dialog"
          aria-label="Aqua ERP AI assistance"
        >
          {/* Backdrop exists only on phones, where the panel covers the screen. */}
          <button
            type="button"
            aria-label="Close assistant"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] sm:hidden"
          />

          <div className="relative flex h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-slate-50 shadow-2xl sm:h-[min(660px,82vh)] sm:w-[430px] sm:rounded-2xl">
            <header className="flex items-center gap-2.5 border-b border-slate-200 bg-gradient-to-r from-cyan-600 to-blue-600 px-3 py-2.5 text-white">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/25">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-bold leading-tight">Aqua ERP AI assistance</div>
                <div className="flex items-center gap-1.5 truncate text-[10px] text-cyan-100">
                  <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />
                  Your workspace only · English, हिंदी, తెలుగు
                </div>
              </div>
              {thread.length ? (
                <button
                  type="button"
                  onClick={reset}
                  aria-label="Start a new conversation"
                  className="rounded-lg p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
                className="rounded-lg p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white"
              >
                <ChevronDown className="hidden h-4 w-4 sm:block" />
                <X className="h-4 w-4 sm:hidden" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
              {!thread.length ? (
                <>
                  <BotBubble>
                    <p className="text-[13px] font-semibold text-slate-800">
                      {intro?.greeting || 'Hello!'}
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                      Ask in English, Hindi or Telugu. I read your live records, and I
                      know my way around your screens. I only use your company’s current
                      workspace — ask <em>“how do I…”</em> or{' '}
                      <em>“where is…”</em> and I will give you the tab and the steps.
                      You can also type a lot, invoice or order number.
                    </p>
                  </BotBubble>

                  {Object.entries(grouped).map(([group, items]) => (
                    <div key={group} className="pl-9">
                      <div className="mb-1 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        {group}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((item) => (
                          <Chip
                            key={item.question}
                            label={item.label}
                            onClick={() => ask(item.question)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {intro && !starters.length ? (
                    <p className="pl-9 text-[11px] text-slate-400">
                      No modules are enabled for your login yet, so there is nothing I can
                      look up. Ask your administrator.
                    </p>
                  ) : null}
                </>
              ) : null}

              {thread.map((entry, index) =>
                entry.role === 'user' ? (
                  <div key={index} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-slate-800 px-3 py-2 text-[12px] leading-relaxed text-white shadow-sm">
                      {entry.text}
                    </div>
                  </div>
                ) : (
                  <AnswerBubble key={index} answer={entry.answer} onOpen={openPath} />
                ),
              )}

              {loading ? <TypingBubble /> : null}
            </div>

            {quickChips.length ? (
              <div className="shrink-0 border-t border-slate-200 bg-white/70 px-2.5 pb-1 pt-2">
                <div className="mb-1 px-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                  {thread.length ? 'Ask next' : 'Popular questions'}
                </div>
                {/* One horizontal row rather than a wrapping block: it must never
                    grow tall enough to squeeze the conversation off screen. */}
                <div className="flex gap-1.5 overflow-x-auto pb-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {quickChips.map((chip, index) => (
                    <Chip
                      key={`${chip.question}-${index}`}
                      label={chip.label}
                      tone="accent"
                      onClick={() => ask(chip.question)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                ask();
              }}
              className="flex items-center gap-1.5 border-t border-slate-200 bg-white px-2.5 py-2"
              style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <input
                ref={inputRef}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder={listening ? 'Listening…' : 'Ask about your ERP, or type a number…'}
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-800 outline-none transition focus:border-cyan-400 focus:bg-white"
              />
              {speechSupported ? (
                <button
                  type="button"
                  onClick={startDictation}
                  aria-label={listening ? 'Stop listening' : 'Ask by voice'}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
                    listening
                      ? 'animate-pulse bg-rose-100 text-rose-600'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <Mic className="h-4 w-4" />
                </button>
              ) : null}
              <button
                type="submit"
                disabled={loading || !question.trim()}
                aria-label="Send question"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white transition hover:opacity-90 disabled:opacity-40"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
